import React from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, CircularProgress } from '@mui/material';
import { colors, fonts, spacing, borderRadius, shadows, tableStyles } from '../../constants';
import UserTableRow from './UserTableRow';

/**
 * UserTable Component
 * Main table component with header and body
 */
const UserTable = ({
  users,
  loading,
  totalUsers,
  page,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
}) => {
  // Header cell style with no hover effects
  const headerCellStyle = {
    ...tableStyles.header,
    color: colors.textWhite,
    fontWeight: fonts.weightSemiBold,
    borderBottom: 'none !important',
    borderTop: 'none !important',
    borderLeft: 'none !important',
    borderRight: 'none !important',
    '&:hover': {
      borderBottom: 'none !important',
      borderTop: 'none !important',
      borderLeft: 'none !important',
      borderRight: 'none !important',
      backgroundColor: 'transparent !important',
      outline: 'none',
    },
    '&::before': {
      display: 'none',
    },
    '&::after': {
      display: 'none',
    },
  };

  return (
    <Paper sx={{ borderRadius: borderRadius.md, boxShadow: shadows.md, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: spacing.sm, sm: spacing.md },
          py: spacing.sm,
        background: colors.primaryDark,
          color: colors.textWhite,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: `${borderRadius.md} ${borderRadius.md} 0 0`,
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          background: colors.primaryTint,
          filter: 'blur(40px)',
          opacity: 0.9,
            zIndex: 0,
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: fonts.weightSemiBold,
            fontSize: { xs: fonts.fontSizeXs, sm: fonts.fontSizeSm },
            letterSpacing: '0.02em',
            position: 'relative',
            zIndex: 1,
          }}
        >
          Users {totalUsers > 0 && `(${totalUsers})`}
        </Typography>
      </Box>
      <TableContainer 
        sx={{ 
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 350px)',
          overflow: 'auto',
        }}
      >
        <Table sx={{ minWidth: { xs: 800, sm: '100%' } }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <TableRow 
              sx={{ 
                backgroundColor: colors.primaryDark,
                '&:hover': { backgroundColor: colors.primaryDark },
                '& .MuiTableCell-root': {
                  borderBottom: 'none !important',
                  borderTop: 'none !important',
                  borderLeft: 'none !important',
                  borderRight: 'none !important',
                  backgroundColor: colors.primaryDark,
                  '&:hover': { borderBottom: 'none !important', borderTop: 'none !important', borderLeft: 'none !important', borderRight: 'none !important' },
                },
              }}
            >
              <TableCell sx={headerCellStyle}>User</TableCell>
              <TableCell sx={headerCellStyle}>Email</TableCell>
              <TableCell sx={headerCellStyle}>Role</TableCell>
              <TableCell sx={headerCellStyle}>Region</TableCell>
              <TableCell sx={headerCellStyle}>Status</TableCell>
              <TableCell sx={headerCellStyle}>Created Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <UserTableRow key={user._id} user={user} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalUsers}
        page={page}
        onPageChange={onChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderTop: `1px solid ${colors.borderDefault}`,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: fonts.fontSizeXs,
            color: colors.textSecondary,
          },
          '& .MuiIconButton-root': {
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: colors.backgroundSoft,
            },
          },
        }}
      />
    </Paper>
  );
};

export default UserTable;

